import { create } from "zustand";
import {
  Chat,
  ChatContainer,
  ChatFilesType,
  ChatMessageId,
  ChatStatus,
  Room,
  UpdateFn,
  Updater,
} from "../../types";
import { ChatApi } from "../../api";
import useUserStore from "../user-store";
import useSocketStore from "../socket-store";
import { getExt } from "../../utils";
import useProgressStore from "../progress-store";
import useAlertStore from "../alert-store";

interface State {
  rooms: Room[];
  selectedRooms: [Room | null, Room | null];
  chats: ChatContainer;
  baseId: number;
  loadedImgs: string[];
  pages: [number, number];
  newMsgs: number;
}

interface Action {
  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>) => void;
  setRooms: UpdateFn<Room[]>;
  setLoadedImgs: UpdateFn<string[]>;
  setPages: UpdateFn<[number, number]>;
  setNewMsgs: UpdateFn<number>;

  getId: () => number;

  setSelectedRoom: (idx: number, room: Room) => void;
  setChats: (roomId: string, chats: Array<Chat> | null) => void;
  appendChats: (roomId: string, chats: Array<Chat>) => void;
  fetchChats: (force?: boolean) => void;

  sendChat: (roomIdx: number, content: string, files: FileList | null) => void;
  updateChatStatus: (chat: ChatMessageId, status: ChatStatus) => void;
  addChat: (roomId: string, chat: Chat) => void;
  rmChat: (roomId: string, chatId: string) => void;
  updateChat: (roomId: string, chat: Chat) => void;

  imgLoaded: (filename: string) => void;
  isImgLoaded: (filename: string) => boolean;

  setPageAt: (idx: number, page: number) => void;
  nextPage: (idx: number) => void;
}

const useChatStore = create<State & Action>((set, get) => ({
  rooms: [],
  selectedRooms: [null, null],
  chats: {},
  baseId: 1,
  loadedImgs: [],
  pages: [1, 1],
  newMsgs: 0,

  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>): void => {
    if (typeof updater === "function") {
      set((state) => ({ [key]: updater(state[key]) }));
    } else {
      set({ [key]: updater });
    }
  },

  setRooms: (rooms) => {
    get().setter("rooms", rooms);
  },

  setLoadedImgs: (imgs) => {
    get().setter("loadedImgs", imgs);
  },

  setPages: (pages) => {
    get().setter("pages", pages);
  },

  setNewMsgs: (newMsgs) => {
    get().setter("newMsgs", newMsgs);
  },

  getId: () => {
    let res = get().baseId;
    set({ baseId: res + 1 });
    return res;
  },

  setSelectedRoom: (idx, room) => {
    if (idx > get().selectedRooms.length - 1 || idx < 0) return;

    let r = get().selectedRooms;
    r[idx] = room;

    let pages = get().pages;
    let p: number = 1;

    for (let i = 0; i < r.length; i++) {
      if (i === idx) continue;
      if (r[i]?._id.$oid === room._id.$oid) {
        p = pages[i];
      }
    }

    pages[idx] = p;

    set({ selectedRooms: r, pages });

    get().fetchChats();
  },

  setChats: (roomId, chats) => {
    set((state) => ({ chats: { ...state.chats, [roomId]: chats } }));
  },

  appendChats: (roomId, chats) => {
    set((state) => ({
      chats: {
        ...state.chats,
        [roomId]: !state.chats[roomId]
          ? chats
          : [...chats, ...state.chats[roomId]!],
      },
    }));
  },

  fetchChats: (force = false) => {
    let prevRoom: Room | null = null;
    get().selectedRooms.forEach((room) => {
      if (
        (!force && room && !get().chats[room._id.$oid]?.length) ||
        (force && room)
      ) {
        if (room._id.$oid === prevRoom?._id.$oid) return;
        prevRoom = room;
        ChatApi.getRoomChats(room._id.$oid)
          .then((chats) => {
            get().setChats(room._id.$oid, chats.messages);
          })
          .catch((err) => console.log(err));
      }
    });
  },

  sendChat: (roomIdx, content, files) => {
    let _chat: Chat;
    let id = get().getId();

    let room = get().selectedRooms[roomIdx];
    if (!room) return;
    let roomId = room._id.$oid;

    _chat = {
      _id: { $oid: id.toString() },
      content,
      date: Date.now(),
      status: ChatStatus.Waiting,
      from: { $oid: useUserStore.getState().user?._id.$oid || "" },
      files: !files
        ? []
        : (Array.from(files).map((file) => ({
            name: URL.createObjectURL(file),
            ext: getExt(file.name),
            realName: file.name,
            local: true,
          })) as ChatFilesType),
    };

    if (files)
      Array.from(files).forEach((file) => {
        get().imgLoaded(file.name);
      });

    get().addChat(roomId, _chat);

    if (!files) {
      useSocketStore
        .getState()
        .sockDispatch("new_message", { chat: roomId, content, id });
    } else {
      ChatApi.sendChat(roomId, content, files, id)
        .catch((err) => {
          useAlertStore
            .getState()
            .pushAlert(err.msg || "unknown error", "error");
          get().rmChat(roomId, id.toString());
        })
        .finally(() => {
          setTimeout(() => {
            Array.from(files).forEach((file) => {
              useProgressStore.getState().rmProgress(file.name);
            });
          }, 500);
        });
    }
  },

  updateChatStatus: (chat, status) => {
    const { chat: room, id, message } = chat;

    get().setter("chats", (prev) => {
      let chats = prev[room.$oid];
      if (chats) {
        for (let i = 0; i < chats.length; i++) {
          if (chats[i]._id.$oid !== id.toString()) continue;
          chats[i] = message;
          chats[i].status = status;
          break;
        }
        prev[room.$oid] = chats;
      }
      return { ...prev };
    });
  },

  addChat: (roomId, chat) => {
    get().setter("chats", (prev) => {
      let chats = prev[roomId];
      if (chats) {
        chats.push(chat);
      }
      prev[roomId] = chats;
      return { ...prev };
    });
  },

  rmChat: (roomId, chatId) => {
    get().setter("chats", (prev) => {
      let chats = prev[roomId];
      if (chats) {
        chats = chats.filter((c) => c._id.$oid !== chatId);
      }
      prev[roomId] = chats;
      return { ...prev };
    });
  },

  updateChat: (roomId, chat) => {
    get().setter("chats", (prev) => {
      let chats = prev[roomId];
      if (chats) {
        for (let i = 0; i < chats.length; i++) {
          if (chats[i]._id.$oid !== chat._id.$oid) continue;
          chats[i] = chat;
          break;
        }
        prev[roomId] = chats;
      }
      return { ...prev };
    });
  },

  imgLoaded: (filename) => {
    set((state) => ({ loadedImgs: [...state.loadedImgs, filename] }));
  },

  isImgLoaded: (filename) => {
    let res = get().loadedImgs.filter((img) => img === filename);
    return res.length ? true : false;
  },

  setPageAt: (idx, page) => {
    if (idx < 0 || idx > get().pages.length - 1) return;

    get().setPages((prev) => {
      get().selectedRooms.forEach((room, i) => {
        if (room?._id.$oid === get().selectedRooms[idx]?._id.$oid) {
          prev[i] = page;
        }
      });
      return [...prev];
    });

    let room = get().selectedRooms[idx];
    if (!room) return;

    ChatApi.getRoomChats(room._id.$oid, page, get().newMsgs)
      .then((chats) => {
        if (!room) return;
        get().appendChats(room._id.$oid, chats.messages);
      })
      .catch((err) => {
        console.log(err);
      });
  },

  nextPage: (idx) => {
    get().setPageAt(idx, get().pages[idx] + 1);
  },
}));

export default useChatStore;
